import { QueryClient } from '@tanstack/react-query';
import { getGetCartQueryKey } from '@unity/commerce-bridge';
import { useEffect, useState } from 'react';
import { isCartOrganizationStale } from './cartChanged';
import {
  configureCommerceReferenceQueries,
  shareCommerceReferenceQueries,
} from './commerceReferenceQueries';

/** commerce-core's cart query, `getGetCartQueryKey(id)`, whatever the id. */
const CART_QUERY_KEY = getGetCartQueryKey('').slice(0, -1);

// Once another tab has moved the cart to another org, a focus or reconnect
// reread for this page's org would move it back.
const isCartOrganizationCurrent = () => !isCartOrganizationStale();

/**
 * A page's commerce-core QueryClient. With MULTI_CART on, the header
 * mini-cart runs commerce-core too, so the org and user context are cached
 * for the page and shared with it (CPS-7696); off, react-query's defaults
 * are kept. Read once, at mount: the flags are rendered on the server.
 */
export const useCommerceQueryClient = (
  shareReferenceQueries: boolean
): QueryClient => {
  const [queryClient] = useState(() => {
    const client = new QueryClient();
    if (shareReferenceQueries) {
      configureCommerceReferenceQueries(client);
      client.setQueryDefaults(CART_QUERY_KEY, {
        refetchOnWindowFocus: isCartOrganizationCurrent,
        refetchOnReconnect: isCartOrganizationCurrent,
      });
    }
    return client;
  });
  useEffect(
    () =>
      shareReferenceQueries
        ? shareCommerceReferenceQueries(queryClient)
        : undefined,
    [shareReferenceQueries, queryClient]
  );
  return queryClient;
};
